import {
  GRID_COLUMNS,
  createSectionColumnId,
  equalColumnSpans,
  getColumnSpanValues,
  getSpanAt,
} from './proposalSectionColumns';
import {
  collectLeafColumnIds,
  defaultNestedLayout,
  flatRowToNested,
  getMainColumnSpanValues,
  getRowColumnSpanValues,
  migrateVerticalToNested,
} from './proposalSectionNested';

export const SECTION_PRESET_ROW = 'row';
export const SECTION_PRESET_NESTED = 'nested';

/** @deprecated ancien preset — migré vers nested */
export const SECTION_PRESET_VERTICAL = 'vertical';

export const SECTION_PRESET_OPTIONS = [
  { value: SECTION_PRESET_ROW, label: 'Ligne' },
  { value: SECTION_PRESET_NESTED, label: 'Grille imbriquée' },
];

/**
 * Applique un preset → sectionColumns complet.
 */
export function applySectionPreset(preset, options = {}) {
  const prev = options.previousSectionColumns;
  const prevLeafIds = prev?.nested
    ? collectLeafColumnIds(prev.nested)
    : (prev?.columnIds || []);

  if (preset === SECTION_PRESET_NESTED || preset === SECTION_PRESET_VERTICAL) {
    let nested;
    if (preset === SECTION_PRESET_VERTICAL) {
      nested = migrateVerticalToNested(options.verticalColumnCount ?? 3);
    } else if (prevLeafIds.length > 0) {
      nested = flatRowToNested(prevLeafIds, prev?.columnSpans);
    } else {
      nested = defaultNestedLayout();
    }
    return {
      preset: SECTION_PRESET_NESTED,
      nested,
    };
  }

  const columnCount = Math.max(
    1,
    Math.min(6, options.columnCount ?? Math.max(prevLeafIds.length, 2)),
  );
  const columnIds = prevLeafIds.length > 0
    ? (prevLeafIds.length >= columnCount
      ? prevLeafIds.slice(0, columnCount)
      : [
        ...prevLeafIds,
        ...Array.from(
          { length: columnCount - prevLeafIds.length },
          () => createSectionColumnId(),
        ),
      ])
    : Array.from({ length: columnCount }, () => createSectionColumnId());

  return {
    preset: SECTION_PRESET_ROW,
    columnIds,
    columnSpans: equalColumnSpans(columnIds.length),
  };
}

/**
 * Galerie de dispositions prêtes à l'emploi.
 * `row` : spans md de chaque colonne (une seule ligne).
 * `mains` : colonnes principales — chacune = liste de lignes, chaque ligne =
 * spans md de ses colonnes. `mainSpans` : largeurs md des colonnes principales
 * (sans la dernière, calculée).
 */
export const SECTION_LAYOUT_PRESETS = [
  { key: 'one', label: '1 colonne', row: [12] },
  { key: 'two-equal', label: '2 colonnes', row: [6, 6] },
  { key: 'two-wide-left', label: 'Large + étroite', row: [8, 4] },
  { key: 'two-wide-right', label: 'Étroite + large', row: [4, 8] },
  { key: 'three-equal', label: '3 colonnes', row: [4, 4, 4] },
  { key: 'four-equal', label: '4 colonnes', row: [3, 3, 3, 3] },
  {
    key: 'leading-left',
    label: 'Grand bloc + pile',
    mainSpans: [8],
    mains: [[[12]], [[12], [12], [12]]],
  },
  {
    key: 'leading-right',
    label: 'Pile + grand bloc',
    mainSpans: [4],
    mains: [[[12], [12], [12]], [[12]]],
  },
];

/** Paires de dispositions miroir (grand bloc à gauche ↔ à droite). */
export const SECTION_LAYOUT_MIRRORS = {
  'two-wide-left': 'two-wide-right',
  'two-wide-right': 'two-wide-left',
  'leading-left': 'leading-right',
  'leading-right': 'leading-left',
};

/**
 * Ordre des ids de zones pour passer d'une disposition à son miroir :
 * l'ordre des colonnes principales est inversé, l'ordre interne conservé —
 * le contenu du grand bloc reste dans le grand bloc, la pile reste la pile.
 * À n'utiliser que si la section matche exactement `sourceDef`
 * (sectionMatchesLayoutPreset) : le nombre de feuilles correspond alors.
 */
export function reorderIdsForMirror(sourceDef, prevIds) {
  if (sourceDef.row) return [...prevIds].reverse();

  const groups = [];
  let i = 0;
  sourceDef.mains.forEach((rows) => {
    const count = rows.reduce((n, spans) => n + spans.length, 0);
    groups.push(prevIds.slice(i, i + count));
    i += count;
  });
  return [...groups.reverse().flat(), ...prevIds.slice(i)];
}

function toPartialSpans(mdSpans) {
  return mdSpans.slice(0, -1).map((md) => ({ base: GRID_COLUMNS, md }));
}

/** Spans md complets d'une définition (dernière colonne = reste). */
export function presetFullMainSpans(def) {
  const n = def.mains.length;
  if (n <= 1) return [GRID_COLUMNS];
  const partial = def.mainSpans || [];
  const sum = partial.reduce((a, b) => a + b, 0);
  return [...partial, Math.max(1, GRID_COLUMNS - sum)];
}

/**
 * Construit un sectionColumns depuis une définition de la galerie.
 * Réutilise les ids de zones existants dans l'ordre : le contenu des
 * premières colonnes est conservé quand on change de disposition.
 */
export function buildSectionLayout(def, previousSectionColumns, { reuseIds } = {}) {
  const prevIds = reuseIds ?? collectSectionZoneIds(previousSectionColumns);
  let idx = 0;
  const nextId = () => {
    const id = prevIds[idx];
    idx += 1;
    return id != null ? id : createSectionColumnId();
  };

  if (def.row) {
    return {
      preset: SECTION_PRESET_ROW,
      columnIds: def.row.map(() => nextId()),
      columnSpans: toPartialSpans(def.row),
    };
  }

  return {
    preset: SECTION_PRESET_NESTED,
    nested: {
      mainColumnSpans: (def.mainSpans || []).map((md) => ({ base: GRID_COLUMNS, md })),
      mainColumns: def.mains.map((rows) => ({
        id: createSectionColumnId(),
        rows: rows.map((spans) => ({
          id: createSectionColumnId(),
          columnSpans: spans.length > 1 ? toPartialSpans(spans) : [],
          columns: spans.map(() => ({ id: nextId() })),
        })),
      })),
    },
  };
}

/** La section correspond-elle (spans md) à une définition de la galerie ? */
export function sectionMatchesLayoutPreset(resolved, def) {
  if (def.row) {
    if (resolved.preset !== SECTION_PRESET_ROW) return false;
    const spans = getColumnSpanValues(resolved.columnIds, resolved.columnSpans);
    return spans.length === def.row.length
      && spans.every((s, i) => s === def.row[i]);
  }

  if (resolved.preset !== SECTION_PRESET_NESTED || !resolved.nested) return false;
  const mains = resolved.nested.mainColumns || [];
  if (mains.length !== def.mains.length) return false;

  const mainSpans = getMainColumnSpanValues(resolved.nested).map((s) => getSpanAt(s, 'md'));
  const defMainSpans = presetFullMainSpans(def);
  if (!mainSpans.every((s, i) => s === defMainSpans[i])) return false;

  return mains.every((mc, mi) => {
    const rows = mc.rows || [];
    if (rows.length !== def.mains[mi].length) return false;
    return rows.every((row, ri) => {
      const defSpans = def.mains[mi][ri];
      const rowSpans = getRowColumnSpanValues(row).map((s) => getSpanAt(s, 'md'));
      return rowSpans.length === defSpans.length
        && rowSpans.every((s, i) => s === defSpans[i]);
    });
  });
}

/** La section ne matche aucun preset de la galerie → disposition personnalisée. */
export function isSectionLayoutCustom(resolved) {
  return !SECTION_LAYOUT_PRESETS.some((def) => sectionMatchesLayoutPreset(resolved, def));
}

/** Ids de colonnes retirés entre deux layouts (nettoyage zones Puck). */
export function getRemovedColumnIds(previousIds, nextIds) {
  const nextSet = new Set(nextIds);
  return (previousIds || []).filter((id) => !nextSet.has(id));
}

/** Tous les ids zones d'un sectionColumns (row ou nested). */
export function collectSectionZoneIds(sectionColumns) {
  if (!sectionColumns) return [];
  if (sectionColumns.preset === SECTION_PRESET_NESTED && sectionColumns.nested) {
    return collectLeafColumnIds(sectionColumns.nested);
  }
  return sectionColumns.columnIds || [];
}
